import { CoverageTypeEnum } from "@platform/types";

import { accidentTypedAmountFields } from "./locs/accidentTypedAmount";
import { basicLifeTypedAmountFields } from "./locs/basicLifeTypedAmount";
import { cancerTypedAmountFields } from "./locs/cancerTypedAmount";
import { criticalIllnessTypedAmountFields } from "./locs/criticalIllnessTypedAmount";
import { dentalHmoTypedAmountFields } from "./locs/dentalHmoTypedAmount";
import { dentalPpoTypedAmountFields } from "./locs/dentalPpoTypedAmount";
import { fmlTypedAmountFields } from "./locs/fmlTypedAmount";
import { hospitalTypedAmountFields } from "./locs/hospitalTypedAmount";
import { idiTypedAmountFields } from "./locs/idiTypedAmount";
import { ltdTypedAmountFields } from "./locs/ltdTypedAmount";
import { stdTypedAmountFields } from "./locs/stdTypedAmount";
import { visionTypedAmountFields } from "./locs/visionTypedAmount";
import { volLifeTypedAmountFields } from "./locs/volLifeTypedAmount";
import { wholeLifeTypedAmountFields } from "./locs/wholeLifeTypedAmount";

import type { TypedAmountSpec } from "./typedAmountTypes";

/**
 * Per-coverage registries of typed-amount specs. The Quotes tab folds
 * each `{type, amount}` pair into a single row keyed by the type field;
 * the standalone amount row is hidden because the merged value is now
 * shown in the type row via `cellFormatOverride` and edited via the
 * typed-amount popup in `cellEditorOverride`.
 */
const TYPED_AMOUNT_BY_COVERAGE: Partial<
  Record<CoverageTypeEnum, Record<string, TypedAmountSpec>>
> = {
  [CoverageTypeEnum.ACCIDENT]: accidentTypedAmountFields,
  [CoverageTypeEnum.BASIC_LIFE_ADND]: basicLifeTypedAmountFields,
  [CoverageTypeEnum.CANCER]: cancerTypedAmountFields,
  [CoverageTypeEnum.CRITICAL_ILLNESS]: criticalIllnessTypedAmountFields,
  [CoverageTypeEnum.DENTAL_HMO]: dentalHmoTypedAmountFields,
  [CoverageTypeEnum.DENTAL_PPO]: dentalPpoTypedAmountFields,
  [CoverageTypeEnum.FMLA]: fmlTypedAmountFields,
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: hospitalTypedAmountFields,
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]: idiTypedAmountFields,
  [CoverageTypeEnum.LTD]: ltdTypedAmountFields,
  [CoverageTypeEnum.STD]: stdTypedAmountFields,
  [CoverageTypeEnum.VISION]: visionTypedAmountFields,
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: volLifeTypedAmountFields,
  [CoverageTypeEnum.WHOLE_LIFE]: wholeLifeTypedAmountFields,
};

/**
 * Extra sibling keys to collapse per coverage: typeKey → sibling benefitKeys
 * whose rows have `benefit.nested = true` (so they get dropped by the filter
 * pass) but whose column values must be accessible to the cell format override
 * via `__collapsedSiblings`.
 *
 * Example: `initialDiagnosisWaitingPeriodDuration` is built with
 * `buildTypeDurationBenefitPair` which sets `nested: true` on the duration
 * row. The formatter reads `row.initialDiagnosisWaitingPeriodDuration` via
 * the collapsed-siblings proxy, so we must stash it here before dropping it.
 */
const SIBLING_COLLAPSE_BY_COVERAGE: Partial<
  Record<CoverageTypeEnum, Record<string, string[]>>
> = {
  [CoverageTypeEnum.CANCER]: {
    initialDiagnosisWaitingPeriodType: [
      "initialDiagnosisWaitingPeriodDuration",
    ],
    preExistingConditionType: ["preExistingConditionMonthsDetail"],
  },
  [CoverageTypeEnum.ACCIDENT]: {
    ageReductionScheduleType: ["ageReductionScheduleSchedules"],
  },
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: {
    preExistingConditionType: ["preExistingConditionMonthsDetail"],
  },
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: {
    // benefit descriptions: the amount is a scalar or a list depending on the
    // type, so they route to BenefitDescriptionCellEditor, not a typed amount
    benefitDescriptionEmployeeType: ["benefitDescriptionEmployeeAmount"],
    benefitDescriptionSpouseType: ["benefitDescriptionSpouseAmount"],
    // multi-amount pairs: cannot collapse to a single TypedAmountCellEditor
    benefitMaximumEmployeeType: [
      "benefitMaximumEmployeeDollarAmount",
      "benefitMaximumEmployeeSalaryMultiplier",
    ],
    guaranteeIssueEmployeeType: [
      "guaranteeIssueEmployeeSalaryMultiplier",
      "guaranteeIssueEmployeeDollarAmount",
      "guaranteeIssueEmployeeAdditionalAmount",
      "guaranteeIssueEmployeeAgesDetails",
    ],
    guaranteeIssueSpouseType: [
      "guaranteeIssueSpouseSalaryMultiplier",
      "guaranteeIssueSpouseDollarAmount",
      "guaranteeIssueSpouseAdditionalAmount",
      "guaranteeIssueSpouseAgesDetails",
    ],
    // The existing v5 pair, plus two independent extended-field pairs
    // (Employee, Spouse) unique to Voluntary Life (CD-2555).
    ageReductionScheduleType: ["ageReductionScheduleSchedules"],
    ageReductionsEe: ["employeeAgeReductionSchedules"],
    ageReductionsSp: ["spouseAgeReductionSchedules"],
  },
  [CoverageTypeEnum.BASIC_LIFE_ADND]: {
    // multi-amount pairs: cannot collapse to a single TypedAmountCellEditor
    benefitAmountType: [
      "benefitAmountDollarAmount",
      "benefitAmountSalaryMultiplier",
    ],
    benefitMaximumType: [
      "benefitMaximumDollarAmount",
      "benefitMaximumSalaryMultiplier",
    ],
    guaranteeIssueType: [
      "guaranteeIssueDollarAmount",
      "guaranteeIssueSalaryMultiplier",
      "guaranteeIssueAdditionalAmount",
    ],
    ageReductionScheduleType: ["ageReductionScheduleSchedules"],
  },
  [CoverageTypeEnum.LTD]: {
    // multi-amount pairs: cannot collapse to a single TypedAmountCellEditor
    benefitAmountType: ["benefitAmountAmount", "benefitAmountMinimum"],
    maximumAmountType: ["maximumAmountDollarAmount", "maximumAmountPercent"],
    // string sibling — display-only, saved as-is
    preExistingConditionType: ["preExistingConditionMonthsDetail"],
    // minimumAmount is nested (buildTypeAmountBenefitPair default) — without
    // this entry it's silently dropped by the nested-row filter below instead
    // of being folded into the type row's popup (same gap CD-2552 hit for
    // STD's minimumBenefit).
    minimumAmountType: ["minimumAmount"],
  },
  [CoverageTypeEnum.STD]: {
    // multi-amount pairs: cannot collapse to a single TypedAmountCellEditor
    benefitAmountType: ["benefitAmountAmount", "benefitAmountMinimum"],
    maximumAmountType: ["maximumAmountDollarAmount", "maximumAmountPercent"],
    // string sibling — display-only, saved as-is
    preExistingConditionType: ["preExistingConditionMonthsDetail"],
    // CD-2552: minimumBenefitAmount is nested (buildTypeAmountBenefitPair
    // default) — without this entry it's silently dropped by the nested-row
    // filter below instead of being folded into the type row's popup.
    minimumBenefitType: ["minimumBenefitAmount"],
  },
  [CoverageTypeEnum.VISION]: {
    examFrequency: ["lensesFrequency", "framesFrequency"],
    standardFramesType: [
      "additionalDiscountsOnFramesType",
      "additionalDiscountsOnFramesAmount",
    ],
  },
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]: {
    // string sibling — display-only, saved as-is
    preExistingConditionType: ["preExistingConditionMonthsDetail"],
  },
  [CoverageTypeEnum.WHOLE_LIFE]: {
    // benefit amount: scalar or list depending on the type, so it routes to
    // BenefitDescriptionCellEditor, not a typed amount
    employeeBenefitAmountType: ["employeeBenefitAmountAmount"],
    // multi-amount pair: cannot collapse to a single TypedAmountCellEditor
    employeeBenefitMaximumType: [
      "employeeBenefitMaximumDollarAmount",
      "employeeBenefitMaximumSalaryMultiplier",
    ],
  },
  [CoverageTypeEnum.CRITICAL_ILLNESS]: {
    // multi-amount pairs: cannot collapse to a single TypedAmountCellEditor
    employeeBenefitDescriptionType: [
      "employeeBenefitDescriptionAmount",
      "employeeBenefitDescriptionMinimum",
    ],
    spouseBenefitDescriptionType: [
      "spouseBenefitDescriptionAmount",
      "spouseBenefitDescriptionMinimum",
    ],
    childBenefitDescriptionType: [
      "childBenefitDescriptionAmount",
      "childBenefitDescriptionMinimum",
    ],
    ageReductionScheduleType: ["ageReductionScheduleSchedules"],
    // string sibling — display-only, saved as-is
    preExistingConditionType: ["preExistingConditionMonthsDetail"],
  },
};

/** Benefit key for the funding-type row (e.g. "Fully Insured"). */
export const FUNDING_TYPE_KEY = "fundingType";

const QUOTES_HIDDEN_BENEFIT_KEYS = new Set([
  "currentCarrierName",
  "currentCommissionType",
  "currentCommissionAmount",
  "requestedCommissionType",
  "requestedCommissionAmount",
  // Vol Life FE-only dependent toggles — Build-tab controls, not PropelX rows.
  "spouseBenefitsIncluded",
  "childBenefitsIncluded",
  // Vol Life child benefit descriptions: Build's three fixed age-band rows are
  // replaced on Present by the single flexible `childBenefitAmountsByAge` row
  // (CD-1976), so the fixed type rows are hidden here. Their `*Amount` siblings
  // and `childLimitingAge` are nested and dropped by the nested-row filter.
  "benefitDescriptionChildDependentAgesBirthTo14DaysType",
  "benefitDescriptionChildDependentAges15DaysTo6MonthsType",
  "benefitDescriptionChildDependentAges6MonthsToLimitingAgeType",
  // Basic Life / Critical Illness — Build-tab control, not a PropelX row.
  "dependentBenefitsIncluded",
]);

/** Benefit key for the eligibility-classes row, shown pinned first on Dental PPO. */
const ELIGIBILITY_CLASSES_KEY = "eligibilityClasses";

/** Coverages where eligibility classes should surface as the first Quotes row. */
const QUOTES_ELIGIBILITY_CLASSES_COVERAGES = new Set([
  CoverageTypeEnum.DENTAL_PPO,
  CoverageTypeEnum.DENTAL_HMO,
  CoverageTypeEnum.VISION,
  CoverageTypeEnum.BASIC_LIFE_ADND,
  CoverageTypeEnum.VOLUNTARY_LIFE_ADND,
  CoverageTypeEnum.STD,
  CoverageTypeEnum.LTD,
  CoverageTypeEnum.HOSPITAL_INDEMNITY,
  CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE,
]);

interface RowLike {
  benefitKey?: string;
  benefit?: {
    nested?: boolean;
    networkDependent?: boolean;
    label?: string;
    editableWhen?: { dependsOn?: string } | null;
  };
  [key: string]: unknown;
}

/**
 * Row-label override for a surviving parent row after sibling collapse, for
 * coverages where the merged row's label should describe the combined
 * benefit rather than the parent's own original label (e.g. Vision's
 * `examFrequency` becomes "Frequency - Exam / Lenses / Frames" once
 * `lensesFrequency`/`framesFrequency` are folded into it). The Build tab is
 * unaffected — this only mutates a cloned `benefit` on the Quotes-tab row.
 */
const ROW_LABEL_OVERRIDE_BY_COVERAGE: Partial<
  Record<CoverageTypeEnum, Record<string, string>>
> = {
  [CoverageTypeEnum.VISION]: {
    examFrequency: "Frequency - Exam / Lenses / Frames",
    standardFramesType: "Frames - After Copay",
  },
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: {
    studentStatusStudentLimitingAgeType: "Student Status Requirement",
  },
};

/**
 * Property name used to attach hidden sibling column-values onto a
 * type-row before its `*Amount` (and other) sibling rows are filtered
 * out. The format override consults this map so it can render the
 * merged display without doing a grid-API lookup against rows that no
 * longer exist.
 *
 * Shape: `{ [siblingBenefitKey]: { [columnField]: value } }`.
 */
export const COLLAPSED_SIBLINGS_KEY = "__collapsedSiblings";

/**
 * Builds a `PlanEditor.transformRowData` callback that flattens the
 * grid to one row per parent benefit. We drop:
 *
 * - Property-child rows whose `benefitKey` contains `.` (e.g.
 *   `hospitalAdmission.amount` — the parent row already shows the
 *   merged value via `cellFormatOverride`).
 * - Nested benefit rows (`benefit.nested === true`) that the LOC
 *   config emits as standalone siblings of their parent (e.g. a
 *   "Duration" or "Months Detail" row sitting under its parent).
 * - Registered typed-amount sibling rows (e.g. cancer's
 *   `diagnosisBenefitAmount`) — the parent type row renders the
 *   merged display and owns the popup editor.
 * - SBS-only excluded rows (carrier, commission, eligibility class)
 *   that should not appear in the Quotes benefits grid.
 *
 * Before dropping each `*Amount` (or other typed-amount sibling) row,
 * its column-value map is copied onto the corresponding `*Type` row at
 * `__collapsedSiblings[siblingKey]` so the format override can resolve
 * sibling values without a grid lookup against the now-missing row.
 */
export const buildCollapseAmountRows = (
  selectedCoverageType: CoverageTypeEnum | undefined,
): ((rowData: unknown[]) => unknown[]) => {
  const registry =
    (selectedCoverageType && TYPED_AMOUNT_BY_COVERAGE[selectedCoverageType]) ||
    {};
  const siblingRegistry =
    (selectedCoverageType &&
      SIBLING_COLLAPSE_BY_COVERAGE[selectedCoverageType]) ||
    {};
  const labelOverrides =
    (selectedCoverageType &&
      ROW_LABEL_OVERRIDE_BY_COVERAGE[selectedCoverageType]) ||
    {};

  // Map of sibling benefitKey → owning type row benefitKey, so we know
  // which type row to attach each sibling's column-values to before
  // dropping it. Populated from two sources:
  // 1. Typed-amount specs — the `amountKey`, plus any `extraKeys`
  //    (additional siblings like hospice's duration) → typeKey.
  // 2. Extra sibling-collapse entries for nested rows whose values must
  //    be accessible via __collapsedSiblings (e.g. duration fields).
  const amountToType = new Map<string, string>();
  for (const spec of Object.values(registry)) {
    amountToType.set(spec.amountKey, spec.typeKey);
    const { extraKeys, textExtra } = spec;
    for (const extra of extraKeys ?? []) {
      amountToType.set(extra.key, spec.typeKey);
    }

    if (textExtra) {
      amountToType.set(textExtra.key, spec.typeKey);
    }
  }
  for (const [typeKey, siblingKeys] of Object.entries(siblingRegistry)) {
    for (const siblingKey of siblingKeys) {
      amountToType.set(siblingKey, typeKey);
    }
  }

  const amountKeys = new Set(amountToType.keys());

  // Reserved row-shape keys that are NOT plan-column data; the rest of
  // the row's enumerable keys are columnField → value pairs.
  const RESERVED = new Set([
    "benefit",
    "benefitKey",
    "_coverage",
    "id",
    "parentId",
    "groupKey",
    "meta",
    "history",
    "values",
    "rowPinned",
    "propertyKey",
    COLLAPSED_SIBLINGS_KEY,
  ]);

  const extractColumnValues = (row: RowLike): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    const isNetworkDependent = row.benefit?.networkDependent === true;
    for (const [key, value] of Object.entries(row)) {
      if (RESERVED.has(key)) continue;
      // Network-dependent rows store values as { planId: { network: value } }.
      // Flatten to { "planId.network": value } so the collapsed-siblings lookup
      // in resolveCollapsedAmount matches the column field format ("{planId}.{network}").
      // Only flatten when the benefit is network-dependent to avoid mangling
      // non-plan object values (e.g. the `benefit` metadata object itself).
      if (
        isNetworkDependent &&
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        for (const [subKey, subValue] of Object.entries(
          value as Record<string, unknown>,
        )) {
          out[`${key}.${subKey}`] = subValue;
        }
      } else {
        out[key] = value;
      }
    }
    return out;
  };

  return (rowData) => {
    // Pass 1: index rows by benefitKey so we can decorate type rows
    // with their sibling amount values before filtering them out.
    const byKey = new Map<string, RowLike>();
    for (const row of rowData) {
      const r = row as RowLike | null | undefined;
      if (r?.benefitKey) byKey.set(r.benefitKey, r);
    }

    for (const [amountKey, typeKey] of amountToType) {
      const amountRow = byKey.get(amountKey);
      const typeRow = byKey.get(typeKey);
      if (!amountRow || !typeRow) continue;
      const existing =
        (typeRow[COLLAPSED_SIBLINGS_KEY] as
          | Record<string, Record<string, unknown>>
          | undefined) ?? {};
      typeRow[COLLAPSED_SIBLINGS_KEY] = {
        ...existing,
        [amountKey]: extractColumnValues(amountRow),
      };
    }

    Object.entries(labelOverrides).forEach(([benefitKey, label]) => {
      const row = byKey.get(benefitKey);
      if (row) row.benefit = { ...row.benefit, label };
    });

    const showEligibilityClasses =
      !!selectedCoverageType &&
      QUOTES_ELIGIBILITY_CLASSES_COVERAGES.has(selectedCoverageType);

    // Pass 2: drop the rows that the parent benefit now subsumes.
    const filtered = rowData.filter((row) => {
      const r = row as RowLike | null | undefined;
      const benefitKey = r?.benefitKey;
      if (!benefitKey) return true;
      if (benefitKey === FUNDING_TYPE_KEY) return true;
      if (benefitKey === ELIGIBILITY_CLASSES_KEY) return showEligibilityClasses;
      if (QUOTES_HIDDEN_BENEFIT_KEYS.has(benefitKey)) return false;
      if (benefitKey.includes(".")) return false;
      if (r?.benefit?.nested === true) return false;
      if (amountKeys.has(benefitKey)) return false;
      return true;
    });

    // Pin eligibility classes as the first benefit row in the SBS grid.
    if (showEligibilityClasses) {
      const pinnedIndex = filtered.findIndex(
        (row) =>
          (row as RowLike | null | undefined)?.benefitKey ===
          ELIGIBILITY_CLASSES_KEY,
      );
      if (pinnedIndex > 0) {
        const [pinnedRow] = filtered.splice(pinnedIndex, 1);
        filtered.unshift(pinnedRow);
      }
    }

    // A cell's editableWhen gate reads its dependsOn value from a sibling grid
    // row; when that row is hidden (e.g. Vol Life's spouse/child "Benefits
    // Included" toggles), the lookup returns undefined and the cell is wrongly
    // treated as disabled and uneditable. Drop the gate here so the SBS cells
    // stay editable — the Build tab still shows those rows and keeps its gate.
    return filtered.map((row) => {
      const dependsOn = (row as RowLike | null | undefined)?.benefit
        ?.editableWhen?.dependsOn;
      if (dependsOn && QUOTES_HIDDEN_BENEFIT_KEYS.has(dependsOn)) {
        const r = row as RowLike;
        return { ...r, benefit: { ...r.benefit, editableWhen: undefined } };
      }
      return row;
    });
  };
};
